/* ──────────────────────────────────────────────────────────────────────────
   NJM Ice-Pack — cold-chain cart logic (Nut Just Milk)
   Rules:
   - Cold-chain items = tag ships:mumbai-only (milks, smoothies, bundles).
     Snacks (ships:pan-india) never get an ice pack.
   - Ice pack qty = ceil(cold-chain bottles / 2)  (1 pack keeps 2 bottles cold).
   - Delivery pincode (cart attribute _validated_pincode / localStorage):
       * Cuffe Parade → Bandra → Khar (400001-400039, 400050-400052) → OPTIONAL.
         Never auto-added, never locked, always removable.
       * Juhu/Andheri outward, suburbs, Thane, Navi Mumbai, Mira-Bhayandar, Panvel
         → ice pack auto-added + locked (can't remove).
       * NO PINCODE YET → treated as OPTIONAL, not compulsory. We do not know the zone,
         so we must not force-add a charge the customer cannot remove. See iceState().
   Drawer body re-renders on every cart change (GET /cart?view=ajax_side_cart)
   and fires the document `cart-update` event — our single hook.
   ────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  if (window.__njmIcepackInit) return; // loaded on both the drawer shell and the cart page — run once
  window.__njmIcepackInit = true;

  var ICE = 45813943107715; // Ice pack and foil bag packaging (Add on) — ₹40

  // Ice pack OPTIONAL — South Mumbai (Colaba → Prabhadevi/Dadar), close to our base.
  // SAFE DEFAULT: ice is compulsory for everything EXCEPT a confirmed South-Mumbai pincode.
  // No pincode entered, a far/suburb pincode, or any non-South pincode => compulsory + locked.
  //
  // ⚠️ The no-ice belt is NOT the ₹50 belt. They are two INDEPENDENT rules that happen to
  // coincide again as of 2026-08-21. They diverged for six days (2026-08-15 → 08-21, while the
  // ₹50 belt reached Andheri) and can diverge again. Do not "resync" this list to fiftyZone()
  // in njm-cart-gate.js, and do NOT delete one because it currently equals the other — a future
  // change to the delivery PRICE must never silently move the cold-chain boundary.
  //   ₹50/free belt (njm-cart-gate.js fiftyZone) = 400001-400039, 400050, 400051, 400052
  //   no-ice belt   (here + njm-cold-chain app)  = 400001-400039, 400050, 400051, 400052
  //
  // 2026-08-21 (Vansh): free delivery at ≥₹1,500 applies to the ₹50 belt only — Cuffe Parade to
  // Khar. Past Khar (400049, 400053-400059, 400069, 400072, 400093, 400096, 400099) is ₹200
  // flat, no free tier: the "Andheri Belt" Shopify Local Delivery zone was moved ₹50 → ₹200 and
  // its conditional pricing removed. Ice was ALREADY compulsory across all of those — unchanged.
  //
  // 2026-08-15 (Vansh): ice is OPTIONAL up to Bandra → Khar. The matching server-side
  // exemption shipped first in the `njm-cold-chain` app (version njm-cold-chain-3) and was
  // verified live — 400016 / 400050 / 400052 now quote without an ice pack — so this list
  // was widened to match it afterwards, in that order.
  //
  // ⚠️ ORDER MATTERS. The hard requirement is enforced server-side by that app's checkout
  // function, which returns NO delivery option at all when it wants a pack and the cart has
  // none. If this list is ever wider than the app's, the cart stops adding a pack checkout
  // still demands and those customers simply cannot check out. Always widen the app first.
  var NO_ICE_MIN = 400001, NO_ICE_MAX = 400039;

  function noIceZone(n) {
    return (n >= NO_ICE_MIN && n <= NO_ICE_MAX) ||
           n === 400050 || n === 400051 || n === 400052;
  }

  // Tri-state. 'unknown' is NOT the same as 'compulsory'.
  //
  // 2026-08-20 (Vansh): "the ice pack all the way till Khar needs to be optional. They need to be
  // able to remove that as well." The old default treated a missing pincode as compulsory, which
  // force-added ceil(bottles/2) packs AND applied .njm-ice-locked (hides remove + qty). A
  // Cuffe-Parade-to-Khar customer who had not yet typed a pincode — i.e. almost everyone, because
  // people add to cart before entering one — was silently charged ₹40/pack with no way to remove it.
  //
  // Unknown now behaves as OPTIONAL: nothing is auto-added, nothing is locked, and the zone asks for
  // the pincode instead. Cold-chain safety is unaffected because it never rested on this default:
  //   1. njm-cart-gate.js refuses to enable "Proceed to checkout" for a cart holding cold items
  //      until a serviceable pincode is entered, so unknown always resolves before an order exists;
  //   2. the moment that pincode lands we re-run reconcile (see the njm-co listeners at the bottom),
  //      so a genuinely compulsory zone gets its pack added there and then;
  //   3. the njm-cold-chain checkout function is the hard server-side backstop on the real address.
  function iceState(pin) {
    var n = pin ? parseInt(pin, 10) : 0;
    if (!n) return 'unknown';
    return noIceZone(n) ? 'optional' : 'compulsory';
  }
  function isCompulsory(pin) { return iceState(pin) === 'compulsory'; }

  var busy = false;

  // 2026-08-24: the number that decides whether to ADD must never come from the DOM.
  // mutate() fetches /cart.js after every change; we keep that and read the ice count
  // from it while it is fresh, so a drawer that has not re-rendered yet can no longer
  // make reconcile() think the cart is empty of packs. See FRESH_MS.
  var lastCart = null, lastCartAt = 0, FRESH_MS = 15000;
  function noteCart(cart) { if (cart && cart.items) { lastCart = cart; lastCartAt = Date.now(); } return cart; }
  function authoritativeIceQty() {
    if (!lastCart || Date.now() - lastCartAt > FRESH_MS) return null; // stale: fall back to DOM
    var q = 0;
    for (var i = 0; i < lastCart.items.length; i++) {
      if (lastCart.items[i].variant_id === ICE) q += lastCart.items[i].quantity;
    }
    return q;
  }

  /* ── NJM-001 pinchange (2026-08-24) ────────────────────────────────────────
     The checkout gate writes the _validated_pincode CART ATTRIBUTE and nothing
     else — no localStorage, no re-render. So the rendered data-validated-pincode
     is the stalest source available, and it used to be read first. The cart
     attribute is the only value both writers agree on, and the one checkout
     validation reads, so it is the authority. */
  function pinFromCart() {
    if (!lastCart || Date.now() - lastCartAt > FRESH_MS) return '';
    var a = lastCart.attributes && lastCart.attributes._validated_pincode;
    return (a && /^\d{6}$/.test(a)) ? a : '';
  }
  /* Who put the ice pack in the cart? '1' = we did, because the zone was compulsory.
     '0' = the customer chose it. Absent = we do not know, and we never delete a line
     we are not sure we added — it is left in place, labelled optional and removable. */
  function iceIsOurs() {
    if (!lastCart || Date.now() - lastCartAt > FRESH_MS) return false;
    return (lastCart.attributes && lastCart.attributes._njm_ice_auto) === '1';
  }
  function markIceOwner(v) {
    return postJSON('/cart/update.js', { attributes: { _njm_ice_auto: v } })
      .then(function () { if (lastCart) { lastCart.attributes = lastCart.attributes || {}; lastCart.attributes._njm_ice_auto = v; } })
      .catch(function () {});
  }
  /* Fetch the cart, THEN reconcile. Anything that can change the zone without a
     cart-update (i.e. the gate) must go through here, or reconcile runs against a
     DOM that still describes the previous pincode. */
  /* Both surfaces must settle. reconcile() below returns immediately when the DRAWER
     zone is absent, which is exactly the case on /cart — so a gate pincode change on the
     cart page reconciled nothing at all. The /cart reconcile lives in a nested scope, so
     it publishes itself here. */
  var settleCart = null;
  var settling = false;
  function settle() {
    if (settling) return;
    settling = true;
    fetchCart()
      .then(function (cart) { noteCart(cart); })
      .catch(function () {})
      .then(function () {
        settling = false;
        reconcile();                          // drawer (no-ops when not rendered)
        if (settleCart) settleCart();         // /cart  (no-ops when not rendered)
      });
  }

  // B. idempotence — never ask for the same thing twice inside this window.
  var lastAsk = null, lastAskAt = 0, ASK_COOLDOWN_MS = 8000;

  // C. absolute ceiling. A cart would need 40 chilled bottles to legitimately want this
  // many packs; anything above it is a bug, not an order.
  var MAX_PACKS = 20;

  // Bottle count only, for the setIce cap — deliberately independent of read() so a
  // mistake in one cannot silently widen the other.
  function readBottles() {
    var n = 0;
    var rows = document.querySelectorAll('#halo-cart-sidebar [data-item-row], [data-item-row]');
    Array.prototype.forEach.call(rows, function (li) {
      if (li.getAttribute('data-icepack') === 'true') return;
      if (li.getAttribute('data-noice') === 'true') return;
      if (li.getAttribute('data-ships') !== 'mumbai-only') return;
      var input = li.querySelector('input[data-cart-quantity], input.cart-item-qty-input');
      n += input ? (parseInt(input.value, 10) || 0) : 0;
    });
    return n;
  }

  function zone() { return document.getElementById('njm-icepack-zone'); }

  function getPin() {
    // NJM-001: truth order reversed. The rendered attribute is written by Liquid at page
    // render and is never updated by the gate, so it is the LAST resort, not the first.
    var p = pinFromCart();
    if (p) return p;
    try { p = localStorage.getItem('deliveryPincode'); } catch (e) {}
    if (p && /^\d{6}$/.test(p)) return p;
    var z = zone();
    p = z && z.getAttribute('data-validated-pincode');
    return (p && /^\d{6}$/.test(p)) ? p : '';
  }

  // Read the rendered drawer: cold-chain bottle count + current ice-pack qty/row
  function read() {
    var bottles = 0, iceQty = 0, iceRow = null;
    var rows = document.querySelectorAll('#halo-cart-sidebar [data-item-row]');
    Array.prototype.forEach.call(rows, function (li) {
      var input = li.querySelector('input[data-cart-quantity]');
      var qty = input ? (parseInt(input.value, 10) || 0) : 0;
      if (li.getAttribute('data-icepack') === 'true') { iceQty += qty; iceRow = li; return; }
      // data-noice rows are Mumbai-only for DELIVERY but are not cold-chain items, so they
      // must not pull an ice pack into the cart.
      if (li.getAttribute('data-noice') === 'true') return;
      if (li.getAttribute('data-ships') === 'mumbai-only') bottles += qty;
    });
    // The DOM is still the only place that knows WHICH lines are cold-chain
    // (data-ships / data-noice are Liquid-rendered; cart.js carries no tags), but the
    // ice COUNT comes from the cart whenever we have a fresh one.
    var authoritative = authoritativeIceQty();
    if (authoritative !== null) iceQty = authoritative;
    return { bottles: bottles, iceQty: iceQty, iceRow: iceRow };
  }

  function rerenderWith(cart) {
    if (window.sharedFunctions && typeof window.sharedFunctions.updateSidebarCart === 'function') {
      window.sharedFunctions.updateSidebarCart(cart);
    }
  }

  // POST JSON with a light retry on 429 (Shopify cart throttle) / transient errors.
  function postJSON(url, body, tries) {
    tries = tries || 0;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if ((r.status === 429 || r.status >= 500) && tries < 4) {
        return new Promise(function (res) { setTimeout(res, 700 * (tries + 1)); }).then(function () { return postJSON(url, body, tries + 1); });
      }
      return r;
    });
  }
  function fetchCart(tries) {
    tries = tries || 0;
    return fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart.js' : '/cart.js')
      .then(function (r) {
        if ((r.status === 429 || r.status >= 500) && tries < 4) {
          return new Promise(function (res) { setTimeout(res, 700 * (tries + 1)); }).then(function () { return fetchCart(tries + 1); });
        }
        return r.json();
      });
  }

  // Mutate the ice-pack line, then re-render the drawer. busy guard avoids loops.
  function mutate(url, body) {
    busy = true;
    setTimeout(function () { busy = false; }, 4000); // safety backstop
    return postJSON(url, body)
      .then(function () { return fetchCart(); })
      .then(function (cart) { noteCart(cart); rerenderWith(cart); return cart; })
      .catch(function () { busy = false; });
  }

  function setIce(target, present) {
    // C. Refuse anything that cannot be right. bottlesNow is recomputed here rather than
    // trusted from the caller, so a bad `need` upstream still cannot get through.
    var cap = Math.min(MAX_PACKS, Math.ceil(readBottles() / 2));
    if (target > cap) {
      if (window.console && console.warn) {
        console.warn('[njm-ice] refusing to set ' + target + ' packs; cap is ' + cap);
      }
      target = cap;
    }
    if (target < 0) target = 0;
    // B. one ask per 8 s for a given target/mode.
    var ask = (present ? 'set:' : 'add:') + target;
    if (ask === lastAsk && Date.now() - lastAskAt < ASK_COOLDOWN_MS) {
      return Promise.resolve(null); // already asked; wait to observe it
    }
    lastAsk = ask; lastAskAt = Date.now();

    // Change/remove an EXISTING ice-pack line via /cart/update.js {updates:{variantId: qty}}.
    // (/cart/change.js with id=<variant id> returns HTTP 400 on this store; update.js by
    //  variant id is reliable and quantity:0 removes the line.)
    // NJM-001: record WHO wanted this pack, so an optional zone can withdraw ours
    // without ever deleting one the customer deliberately chose.
    var owner = (arguments.length > 2 && arguments[2]) ? String(arguments[2]) : '';
    if (present) {
      var body = { updates: { '45813943107715': target } };
      if (owner) body.attributes = { _njm_ice_auto: (target === 0 ? '' : owner) };
      return mutate('/cart/update.js', body);
    }
    return mutate('/cart/add.js', { items: [{ id: ICE, quantity: target }] })
      .then(function (cart) { return owner ? markIceOwner(owner).then(function () { return cart; }) : cart; });
  }

  function reconcile() {
    var z = zone();
    if (!z || busy) return; // zone only exists when the drawer body is rendered & cart non-empty

    var c = read();
    var need = c.bottles > 0 ? Math.ceil(c.bottles / 2) : 0;
    var pin = getPin();
    var compulsory = isCompulsory(pin);

    document.body.classList.remove('njm-ice-compulsory');
    if (c.iceRow) c.iceRow.classList.remove('njm-ice-locked');

    var msg = document.getElementById('njm-ice-msg');
    var add = document.getElementById('njm-ice-add');

    // Snack-only / no cold-chain item → no ice pack
    if (c.bottles === 0) {
      if (c.iceQty > 0) { setIce(0, true); return; } // strip a stray ice pack
      z.style.display = 'none';
      return;
    }
    z.style.display = '';

    if (compulsory) {
      document.body.classList.add('njm-ice-compulsory');
      if (c.iceQty !== need) { setIce(need, c.iceQty > 0, '1'); return; }
      if (c.iceRow) c.iceRow.classList.add('njm-ice-locked');
      if (add) add.style.display = 'none';
      if (msg) msg.innerHTML = pin
        ? '<span class="njm-ice-ic">❄️</span> <strong>Ice pack added — required</strong> to keep your order cold in transit. ' + need + ' pack' + (need > 1 ? 's' : '') + ' · ₹40 each.'
        : '<span class="njm-ice-ic">❄️</span> <strong>Ice pack added — required</strong> to keep your order cold in transit.';
    } else {
      // Optional belt (Cuffe Parade → Khar), or the pincode is not known yet.
      if (add && add.getAttribute('data-orig-label') === null) add.setAttribute('data-orig-label', add.textContent);
      var origLabel = add ? add.getAttribute('data-orig-label') : '';

      // NJM-001 case A: they were in a compulsory zone, we auto-added, and they have now
      // corrected the pincode to somewhere inside the optional belt. The pack is ours, it
      // is no longer required, and leaving it is an overcharge — withdraw it. A pack the
      // CUSTOMER added is never touched here.
      if (c.iceQty > 0 && iceIsOurs()) { setIce(0, true, ''); return; }
      if (c.iceQty >= need) {
        if (add) { add.style.display = 'none'; if (origLabel) add.textContent = origLabel; }
        if (msg) msg.innerHTML = '<span class="njm-ice-ic">❄️</span> Ice pack in your cart — keeps your order cold in transit.';
      } else if (c.iceQty > 0) {
        // 2026-08-20: they opted in and THEN added more chilled items. Previously this branch did
        // nothing at all — the pack count never resized, so they sat under-packed with no hint.
        // Offer the top-up; never auto-add here. Ice is optional in this belt, and silently
        // growing a charge the customer did not ask for is exactly the bug fixed earlier today.
        var short = need - c.iceQty;
        if (add) {
          add.style.display = '';
          add.setAttribute('data-need', need);
          add.textContent = '❄️ Add ' + short + ' more ice pack' + (short > 1 ? 's' : '') + ' · ₹40 each';
        }
        if (msg) msg.innerHTML = '<span class="njm-ice-ic">❄️</span> You have ' + c.iceQty + ' ice pack' +
          (c.iceQty > 1 ? 's' : '') + ' for ' + c.bottles + ' chilled item' + (c.bottles > 1 ? 's' : '') +
          ' — ' + need + ' keeps everything cold the whole way.';
      } else {
        if (add) { add.style.display = ''; add.setAttribute('data-need', need); if (origLabel) add.textContent = origLabel; }
        if (msg) msg.innerHTML = pin
          ? '<span class="njm-ice-ic">❄️</span> Keep your ' + c.bottles + ' item' + (c.bottles > 1 ? 's' : '') + ' cold in transit — add an ice pack.'
          : '<span class="njm-ice-ic">📍</span> Enter your delivery pincode below — an ice pack may be required to keep your order cold.';
      }
    }
  }

  function savePin(pin) {
    pin = (pin || '').replace(/\D/g, '');
    if (!/^\d{6}$/.test(pin)) return;
    try { localStorage.setItem('deliveryPincode', pin); } catch (e) {}
    var z = zone(); if (z) z.setAttribute('data-validated-pincode', pin);
    mutate('/cart/update.js', { attributes: { _validated_pincode: pin } });
  }

  // ── Single hook: drawer re-render fires `cart-update`. busy echo clears the guard. ──
  document.addEventListener('cart-update', function () {
    if (busy) { busy = false; setTimeout(reconcile, 60); return; } // consume our own mutation echo, then settle UI
    reconcile();
  });

  // ── Wiring: pincode save + optional "add ice pack" (event delegation) ──
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t.closest) return;
    if (t.closest('#njm-ice-pin-save')) {
      e.preventDefault();
      var i = document.getElementById('njm-ice-pin-input');
      if (i) savePin(i.value);
    } else if (t.closest('#njm-ice-add')) {
      e.preventDefault();
      var c = read();
      var need = c.bottles > 0 ? Math.ceil(c.bottles / 2) : 1;
      setIce(need, c.iceQty > 0, '0');   // theirs — never auto-withdrawn
    } else if (t.closest('#checkPin') && document.getElementById('zippy_widget_1511')) {
      // PDP: mirror Zippy's checked pincode so the cart already knows the delivery zone
      setTimeout(function () {
        var inp = document.querySelector('#zippy_widget_1511 input#pin');
        var v = inp && (inp.value || '').replace(/\D/g, '');
        if (v && /^\d{6}$/.test(v)) {
          try { localStorage.setItem('deliveryPincode', v); } catch (err) {}
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ attributes: { _validated_pincode: v } })
          });
        }
      }, 350);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && e.target && e.target.id === 'njm-ice-pin-input') {
      e.preventDefault();
      savePin(e.target.value);
    }
  });

  // The checkout gate (njm-cart-gate.js) captures a pincode in its own popup and writes
  // _validated_pincode directly, without re-rendering the drawer — so no `cart-update` fires and
  // reconcile would never learn the zone. Without this, a compulsory-zone customer who first gives
  // their pincode in that popup reaches checkout with no ice pack and is blocked by the function.
  // Re-settle on that path too. reconcile() is idempotent and busy-guarded, so extra calls are free.
  // NJM-001: reconcile() alone was not enough — the gate changes the zone without any
  // cart-update, so the DOM (and therefore the old getPin order) still described the
  // previous pincode. settle() re-reads the cart first.
  function reconcileSoon() { setTimeout(settle, 600); setTimeout(settle, 1500); }
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('[data-co-check], [data-co-change]')) reconcileSoon();
  });
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (t && t.classList && t.classList.contains('njm-co-input') &&
        (t.value || '').replace(/\D/g, '').length === 6) reconcileSoon();
  });

  // Initial settle (drawer may already be rendered)
  if (document.readyState !== 'loading') setTimeout(settle, 400);
  else document.addEventListener('DOMContentLoaded', function () { setTimeout(settle, 400); });

  /* ───────────────────────── FULL CART PAGE (/cart) ─────────────────────────
     Same cold-chain rules, but the cart page re-renders via Ella's <cart-items>
     (cart.js), so mutations here use a simple update.js/add.js + location.reload().
     Shares noIceZone / ICE / postJSON from above. */
  function cartZone() { return document.getElementById('njm-icepack-zone-cart'); }
  if (cartZone()) {
    var cartBusy = false;
    var cartPin = function () {
      var p = pinFromCart();                       // NJM-001: freshest first
      if (p) return p;
      try { p = localStorage.getItem('deliveryPincode'); } catch (e) {}
      if (p && /^\d{6}$/.test(p)) return p;
      var z = cartZone(); p = z && z.getAttribute('data-validated-pincode');
      return (p && /^\d{6}$/.test(p)) ? p : '';
    };
    /* One shot per (direction, pincode, need). cartMutateReload reloads the page, so an
       action that keeps failing would reload forever without this — same shape as the
       existing strip guard. A genuine change of pincode or basket produces a new key and
       is allowed to act again. */
    var cartActOnce = function (key) {
      var K = 'njm_ice_act';
      try { if (sessionStorage.getItem(K) === key) return false; sessionStorage.setItem(K, key); } catch (e) {}
      return true;
    };
    var cartRead = function () {
      var b = 0, iq = 0, ir = null;
      var rows = document.querySelectorAll('#main-cart-items [data-item-row], .cart-list [data-item-row]');
      Array.prototype.forEach.call(rows, function (li) {
        var input = li.querySelector('input[data-cart-quantity]');
        var qty = input ? (parseInt(input.value, 10) || 0) : 0;
        if (li.getAttribute('data-icepack') === 'true') { iq += qty; ir = li; return; }
        if (li.getAttribute('data-noice') === 'true') return;
      if (li.getAttribute('data-ships') === 'mumbai-only') b += qty;
      });
      return { bottles: b, iceQty: iq, iceRow: ir };
    };
    // user-initiated only (Add button / pincode save) — never called from reconcile, so it can't loop
    var cartMutateReload = function (url, body) {
      if (cartBusy) return; cartBusy = true;
      postJSON(url, body).then(function () { location.reload(); }).catch(function () { cartBusy = false; });
    };
    // DISPLAY-ONLY: computes state and updates the UI. Never mutates the cart (loop-proof).
    // Hard enforcement of "compulsory" happens at checkout (validation function); the drawer auto-adds.
    var cartReconcile = function () {
      var z = cartZone(); if (!z) return;
      var c = cartRead();
      var need = c.bottles > 0 ? Math.ceil(c.bottles / 2) : 0;
      var pin = cartPin();
      var compulsory = isCompulsory(pin);
      if (c.iceRow) c.iceRow.classList.remove('njm-ice-locked');
      var msg = document.getElementById('njm-ice-msg-cart');
      var add = document.getElementById('njm-ice-add-cart');
      if (add) add.classList.remove('njm-ice-required');
      // 2026-08-20: a customer who removes every cold item ON THIS PAGE was left holding the
      // ice pack — /cart's reconcile is display-only, so only the drawer ever stripped a stray
      // pack. Verified live: 4 bottles + 2 packs, delete the bottles, and the cart sits at
      // 2 packs / ₹80 with checkout live and a ₹50 quote — ₹130 for nothing but ice.
      // Strip it here too, matching the drawer's bottles===0 branch.
      // Loop guard: cartMutateReload reloads the page, and this runs again on load. If the
      // removal ever fails the second pass would reload forever, so only ever attempt it once
      // per session and otherwise fall through to telling them.
      if (c.bottles === 0) {
        var STRIP_KEY = 'njm_ice_strip_attempted';
        if (c.iceQty > 0) {
          var attempted = false;
          try { attempted = sessionStorage.getItem(STRIP_KEY) === '1'; } catch (e) {}
          if (!attempted) {
            try { sessionStorage.setItem(STRIP_KEY, '1'); } catch (e) {}
            cartMutateReload('/cart/update.js', { updates: { '45813943107715': 0 } });
            return;
          }
          z.style.display = '';
          if (add) add.style.display = 'none';
          if (msg) msg.innerHTML = '<span class="njm-ice-ic">\u2744\uFE0F</span> There are no chilled items left in your cart — you can remove the ice pack above.';
          return;
        }
        try { sessionStorage.removeItem(STRIP_KEY); } catch (e) {}
        z.style.display = 'none';
        return;
      }
      z.style.display = '';
      try { sessionStorage.removeItem('njm_ice_strip_attempted'); } catch (e) {}
      if (compulsory) {
        if (c.iceQty >= need) {
          if (c.iceRow) c.iceRow.classList.add('njm-ice-locked');
          if (add) add.style.display = 'none';
          if (msg) msg.innerHTML = pin
            ? '<span class="njm-ice-ic">❄️</span> <strong>Ice pack added — required</strong> to keep your order cold in transit. ' + need + ' pack' + (need > 1 ? 's' : '') + ' · ₹40 each.'
            : '<span class="njm-ice-ic">❄️</span> <strong>Ice pack added — required</strong> to keep your order cold in transit.';
        } else {
          // NJM-001 case B: /cart was display-only, so changing the pincode INTO a
          // compulsory zone left a chilled cart with no pack and an optional-looking
          // button. Add it, once per (pincode, need); if that ever fails, fall through
          // and tell them rather than reloading forever.
          if (cartActOnce('add:' + pin + ':' + need)) {
            if (c.iceQty > 0) cartMutateReload('/cart/update.js', { updates: { '45813943107715': need }, attributes: { _njm_ice_auto: '1' } });
            else cartMutateReload('/cart/add.js', { items: [{ id: ICE, quantity: need }], attributes: { _njm_ice_auto: '1' } });
            return;
          }
          if (add) { add.style.display = ''; add.classList.add('njm-ice-required'); add.textContent = '❄️ Add ice pack (required) — ₹40 × ' + need; }
          if (msg) msg.innerHTML = '<span class="njm-ice-ic">⚠️</span> An ice pack is <strong>required</strong> to keep your order cold — add it below.';
        }
      } else {
        if (c.iceQty > 0) {
          // NJM-001 case A on /cart: ours, no longer required — withdraw it.
          if (iceIsOurs() && cartActOnce('rm:' + pin)) {
            cartMutateReload('/cart/update.js', { updates: { '45813943107715': 0 }, attributes: { _njm_ice_auto: '' } });
            return;
          }
          if (add) add.style.display = 'none';
          if (msg) msg.innerHTML = '<span class="njm-ice-ic">❄️</span> Ice pack in your cart — keeps your order cold in transit.';
        } else {
          if (add) add.style.display = '';
          if (msg) msg.innerHTML = pin
            ? '<span class="njm-ice-ic">❄️</span> Keep your ' + c.bottles + ' item' + (c.bottles > 1 ? 's' : '') + ' cold in transit — add an ice pack.'
            : '<span class="njm-ice-ic">📍</span> Enter your delivery pincode below — an ice pack may be required to keep your order cold.';
        }
      }
    };
    var cartSavePin = function (pin) {
      pin = (pin || '').replace(/\D/g, ''); if (!/^\d{6}$/.test(pin)) return;
      try { localStorage.setItem('deliveryPincode', pin); } catch (e) {}
      cartMutateReload('/cart/update.js', { attributes: { _validated_pincode: pin } });
    };
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      if (e.target.closest('#njm-ice-pin-save-cart')) { e.preventDefault(); var i = document.getElementById('njm-ice-pin-input-cart'); if (i) cartSavePin(i.value); }
      else if (e.target.closest('#njm-ice-add-cart')) {
        e.preventDefault(); var c = cartRead(); var need = c.bottles > 0 ? Math.ceil(c.bottles / 2) : 1;
        if (c.iceQty > 0) cartMutateReload('/cart/update.js', { updates: { '45813943107715': need }, attributes: { _njm_ice_auto: '0' } });
        else cartMutateReload('/cart/add.js', { items: [{ id: ICE, quantity: need }], attributes: { _njm_ice_auto: '0' } });
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target && e.target.id === 'njm-ice-pin-input-cart') { e.preventDefault(); cartSavePin(e.target.value); }
    });
    settleCart = cartReconcile;   // NJM-001: let settle() reach this surface too
    // NJM-001: without a cart in hand, cartPin() and iceIsOurs() have nothing fresh to
    // read and /cart falls straight back to the stale rendered attribute.
    var cartRun = function () {
      fetchCart().then(function (cart) { noteCart(cart); })
                 .catch(function () {})
                 .then(function () { setTimeout(cartReconcile, 120); });
    };
    if (document.readyState !== 'loading') cartRun(); else document.addEventListener('DOMContentLoaded', cartRun);
    // Re-run only when cart ROWS change (observe .cart-list with childList, NO subtree). cartReconcile
    // never adds/removes .cart-list rows and writes the zone OUTSIDE .cart-list, so it can't re-trigger
    // this — avoids the innerHTML→observer feedback loop. Debounced for safety.
    var clist = document.querySelector('.cart-list');
    if (clist && window.MutationObserver) {
      var deb; new MutationObserver(function () { clearTimeout(deb); deb = setTimeout(cartReconcile, 200); }).observe(clist, { childList: true });
    }
  }
})();

